import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactsPage = () => {
  const { authAxios } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadContacts = async () => {
    try {
      const res = await authAxios.get("/contacts");
      setContacts(res.data.contacts || []);
    } catch (err) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [authAxios]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Importing contacts...");
      const res = await authAxios.post("/contacts/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.dismiss();
      toast.success(res.data.message);
      loadContacts();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "Import failed");
    }
    e.target.value = '';
  };

  const handleDeleteSelected = async () => {
    try {
      for (const id of selectedContacts) {
        await authAxios.delete(`/contacts/${id}`);
      }
      toast.success(`Deleted ${selectedContacts.size} contacts`);
      setSelectedContacts(new Set());
      loadContacts();
    } catch (err) {
      toast.error("Failed to delete contacts");
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteAll = async () => {
    try {
      await authAxios.delete("/contacts");
      toast.success("All contacts deleted");
      setContacts([]);
    } catch (err) {
      toast.error("Failed to delete contacts");
    }
    setShowDeleteConfirm(false);
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedContacts(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedContacts.size === filteredContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.name && c.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) return <Layout><LoadingScreen /></Layout>;

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="contacts-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Contacts</h1>
            <p className="text-slate-500 mt-1">{contacts.length} contacts in your list</p>
          </div>
          
          <div className="flex items-center gap-3">
            <label htmlFor="contact-import" className="cursor-pointer">
              <input
                id="contact-import"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImport}
                className="hidden"
              />
              <Button variant="outline" className="rounded-full" asChild>
                <span><Upload className="w-4 h-4 mr-2" /> Import Contacts</span>
              </Button>
            </label>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100 rounded-2xl mb-6">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Your Contact List</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Contacts uploaded here are automatically saved and can be reused across all your campaigns. 
                  When you upload contacts to a campaign, they're also added here.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {contacts.length === 0 ? (
          <Card className="bg-white border-slate-100 rounded-2xl">
            <CardContent className="py-16 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No contacts yet</h3>
              <p className="text-slate-500 mb-6">Import your first contact list to get started</p>
              <label htmlFor="contact-import-empty" className="cursor-pointer inline-block">
                <input
                  id="contact-import-empty"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImport}
                  className="hidden"
                />
                <Button className="bg-afa-navy hover:bg-afa-navy-dark rounded-full" asChild>
                  <span><Upload className="w-4 h-4 mr-2" /> Import Contacts</span>
                </Button>
              </label>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-slate-100 rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <Input
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 rounded-full"
                />
                {selectedContacts.size > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete ({selectedContacts.size})
                  </Button>
                )}
              </div>
              <p className="text-sm text-slate-500">
                Showing {filteredContacts.length} of {contacts.length}
              </p>
            </CardHeader>
            <CardContent>
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="w-12 py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedContacts.size === filteredContacts.length && filteredContacts.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Added</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.slice(0, 100).map((contact) => (
                      <tr key={contact.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedContacts.has(contact.id)}
                            onChange={() => toggleSelect(contact.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-afa-navy-light rounded-full flex items-center justify-center">
                              <span className="text-sm font-semibold text-afa-navy">
                                {contact.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-slate-900">{contact.email}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{contact.name || "-"}</td>
                        <td className="py-3 px-4 text-slate-500 text-sm">
                          {contact.created_at ? new Date(contact.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-3 px-4 text-slate-500 text-sm">{contact.source || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredContacts.length > 100 && (
                  <div className="p-4 text-center text-slate-500 bg-slate-50 border-t">
                    Showing first 100 of {filteredContacts.length} contacts
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
            <Card className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  Delete Contacts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600">
                  {selectedContacts.size > 0 
                    ? `Are you sure you want to delete ${selectedContacts.size} selected contacts?`
                    : "Are you sure you want to delete ALL contacts?"
                  }
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={selectedContacts.size > 0 ? handleDeleteSelected : handleDeleteAll}
                    className="flex-1 bg-red-600 hover:bg-red-700 rounded-full"
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Analytics Page - Enhanced with campaign list and charts

export default ContactsPage;
